import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await register(name, email, password);
      toast.success("Account created — check your email for a verification code");
      navigate("/verify");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain flex min-h-screen items-center justify-center bg-background px-6 text-white">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center"><Logo className="text-3xl" /></div>
        <div className="glass-panel p-8">
          <h1 className="font-display text-2xl font-bold">Create your account</h1>
          <p className="mt-1 text-sm text-zinc-400">Track progress & chat with us — all in one place.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label className="text-zinc-400">Full Name</Label>
              <Input data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)}
                required className="mt-1.5 rounded-sm border-white/10 bg-black" placeholder="Jane Doe" />
            </div>
            <div>
              <Label className="text-zinc-400">Email</Label>
              <Input data-testid="register-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required className="mt-1.5 rounded-sm border-white/10 bg-black" placeholder="you@company.com" />
            </div>
            <div>
              <Label className="text-zinc-400">Password</Label>
              <PasswordInput testId="register-password" value={password} onChange={(e) => setPassword(e.target.value)}
                required minLength={6} className="mt-1.5 rounded-sm border-white/10 bg-black" placeholder="At least 6 characters" />
            </div>
            {error && <p data-testid="register-error" className="text-sm text-destructive">{error}</p>}
            <Button data-testid="register-submit-btn" type="submit" disabled={loading}
              className="w-full rounded-full bg-primary text-white hover:bg-primary/90">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Account
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-zinc-500">
            Already have an account?{" "}
            <Link to="/login" data-testid="register-to-login" className="text-primary hover:underline">Sign in</Link>
          </p>
        </div>
        <p className="mt-6 text-center">
          <Link to="/" className="font-mono text-xs text-zinc-500 hover:text-zinc-300">← Back to home</Link>
        </p>
      </div>
    </div>
  );
}
